import ReactECharts from 'echarts-for-react';

/**
 * AuditTrailChart_3 — "dark command-deck" variant of AuditTrailChart (blog04).
 * Same real data: documented AI incidents per year (AI Incident Database, as
 * reported by Stanford HAI AI Index 2024–2026 — see AuditTrailChart.tsx header
 * for the per-year citations; mirrors public/blog/ai-incidents-by-year.csv).
 * Opaque slate-900 background, light text, teal accents, subtle gridlines.
 * Confident dashboard feel.
 */

const PALETTE = {
  teal: '#0AB3BF',
  slateMid: '#64748B',
  grid: '#1E293B', // slate-800 — subtle on slate-900
  text: '#F8FAFC',
  textSoft: '#94A3B8',
  surface: '#0F172A', // slate-900, opaque
};

const YEARS = ['2023', '2024', '2025'];
// Documented AI incidents per year — Stanford HAI AI Index 2024/2025/2026.
const INCIDENTS = [123, 233, 362];

/** ONE shared count formatter — tooltip, axis ticks, and bar labels all use it. */
const formatCount = (v: number) => `${v}`;

export const AuditTrailChart_3 = ({ height = 340 }: { height?: number }) => {
  const option = {
    backgroundColor: PALETTE.surface,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(148,163,184,0.08)' } },
      backgroundColor: '#1E293B',
      borderColor: PALETTE.slateMid,
      textStyle: { color: PALETTE.text, fontSize: 13 },
      formatter: (params: { value: number; dataIndex: number }[]) =>
        `${YEARS[params[0].dataIndex]}<br/>documented AI incidents: <b>${formatCount(params[0].value)}</b>`,
    },
    grid: { left: 48, right: 24, top: 48, bottom: 36 },
    xAxis: {
      type: 'category',
      data: YEARS,
      axisLine: { lineStyle: { color: PALETTE.slateMid } },
      axisLabel: { color: PALETTE.textSoft, fontSize: 13, fontWeight: 600 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'documented incidents',
      nameTextStyle: { color: PALETTE.textSoft, fontSize: 11 },
      axisLabel: { color: PALETTE.textSoft, fontSize: 12, formatter: (v: number) => formatCount(v) },
      splitLine: { lineStyle: { color: PALETTE.grid, width: 1 } },
    },
    series: [
      {
        name: 'Documented AI incidents',
        type: 'bar',
        barWidth: '42%',
        itemStyle: { color: PALETTE.teal, borderRadius: [4, 4, 0, 0] },
        data: INCIDENTS,
        label: {
          show: true,
          position: 'top',
          color: PALETTE.text,
          fontSize: 13,
          fontWeight: 700,
          formatter: (p: { value: number }) => formatCount(p.value),
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: PALETTE.textSoft, type: 'dashed', width: 1.5 },
          label: {
            position: 'insideEndTop',
            color: PALETTE.textSoft,
            fontSize: 11,
            formatter: 'under 100 every year until 2022',
          },
          data: [{ yAxis: 100 }],
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-700 bg-slate-900 p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-400 text-center">
        Figure 1. Documented AI incidents nearly tripled in two years (123 → 362). Every incident on this curve is an
        event where someone needed a receipt. Source: Stanford HAI AI Index 2024–2026.
      </p>
      <p className="mt-1 text-label-xs text-slate-500 text-center">
        AI Incident Database backfills historical years; per-year totals can be revised upward (AI Index 2026 note).
      </p>
    </div>
  );
};
